import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { apiFetch, setToken, clearToken } from '../utils/apiFetch';

const AuthContext = createContext(null);
const API = process.env.REACT_APP_BACKEND_URL + '/api';

function formatApiErrorDetail(detail) {
  if (detail == null) return null;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  }
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const r = await apiFetch(`${API}/auth/me`);
      if (!r.ok) throw new Error('Not authenticated');
      const data = await r.json();
      setUser(data);
    } catch {
      setUser(null);
      // Token in localStorage is invalid / expired — clear it so subsequent
      // requests don't keep sending a stale bearer.
      clearToken();
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/myportafolio';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const loginWithEmail = async (email, password) => {
    const r = await apiFetch(`${API}/auth/login-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) {
      const msg = formatApiErrorDetail(data?.detail) || 'Login failed';
      throw new Error(msg);
    }
    if (data?.access_token) setToken(data.access_token);
    setUser(data);
    return data;
  };

  const registerWithEmail = async (email, password, name) => {
    const r = await apiFetch(`${API}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, name }),
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) {
      const msg = formatApiErrorDetail(data?.detail) || 'Registration failed';
      throw new Error(msg);
    }
    if (data?.access_token) setToken(data.access_token);
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await apiFetch(`${API}/auth/logout`, { method: 'POST' });
    } catch {}
    clearToken();
    setUser(null);
    window.location.href = '/myportafolio';
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, loginWithEmail, registerWithEmail, logout, checkAuth, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};
